import type {
  MIExternalTileServerOptions, MITileServerType, MIAuthStrategy,
  MIServerStatus, MICesiumIonAsset, MIArcGISServiceInfo,
} from './types';

export class MIExternalTileServer {
  private options: MIExternalTileServerOptions;

  constructor(options: MIExternalTileServerOptions) {
    this.options = {
      tileSize: 256,
      maxZoom: 22,
      minZoom: 0,
      ...options,
      url: options.url.replace(/\/$/, ''),
    };
  }

  static fromCesiumIon(assetId: number, accessToken: string, options?: Partial<MIExternalTileServerOptions>): MIExternalTileServer {
    return new MIExternalTileServer({
      url: `https://assets.cesium.com/${assetId}`,
      type: 'cesium-ion',
      auth: { type: 'bearer', token: accessToken },
      ...options,
    });
  }

  static fromArcGIS(serviceUrl: string, options?: Partial<MIExternalTileServerOptions>): MIExternalTileServer {
    return new MIExternalTileServer({
      url: serviceUrl.replace(/\/$/, ''),
      type: 'arcgis-map',
      ...options,
    });
  }

  async getTileset(tilesetPath?: string): Promise<any> {
    const url = tilesetPath
      ? `${this.options.url}/${tilesetPath}`
      : `${this.options.url}/tileset.json`;

    const headers: Record<string, string> = { ...this.options.headers };
    this.applyAuth(headers);

    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error(`Failed to fetch tileset: ${response.status}`);
    return response.json();
  }

  getTileUrl(): string {
    switch (this.options.type) {
      case 'xyz':
      case 'tms': {
        let url = this.options.url;
        if (!url.includes('{z}')) {
          url += '/{z}/{x}/{y}.png';
        }
        return url;
      }
      case 'quadkey':
        return `${this.options.url}/{quadkey}`;
      case 'arcgis-map':
      case 'arcgis-image':
        return `${this.options.url}/tile/{z}/{y}/{x}`;
      default:
        return this.options.url;
    }
  }

  get3DTilesUrl(tilesetPath?: string): string {
    return tilesetPath
      ? `${this.options.url}/${tilesetPath}`
      : `${this.options.url}/tileset.json`;
  }

  async listCesiumIonAssets(accessToken: string): Promise<MICesiumIonAsset[]> {
    const response = await fetch('https://api.cesium.com/v1/assets', {
      headers: { 'Authorization': `Bearer ${accessToken}` },
    });
    if (!response.ok) throw new Error(`Failed to list Cesium Ion assets: ${response.status}`);
    const data = await response.json();
    return (data.items || []).map((item: any) => ({
      id: item.id,
      name: item.name,
      description: item.description || '',
      type: item.type,
      status: item.status,
      dateAdded: item.dateAdded,
      bytes: item.bytes || 0,
      attribution: item.attribution,
    }));
  }

  async getArcGISServiceInfo(): Promise<MIArcGISServiceInfo> {
    const url = `${this.options.url}?f=json`;
    const response = await fetch(url, { headers: this.options.headers });
    if (!response.ok) throw new Error(`Failed to fetch ArcGIS service info: ${response.status}`);
    const data = await response.json();
    return {
      serviceDescription: data.serviceDescription || data.description || '',
      mapName: data.mapName || data.name || '',
      layers: (data.layers || []).map((l: any) => ({
        id: l.id,
        name: l.name,
        type: l.type || 'Feature Layer',
        geometryType: l.geometryType,
      })),
      spatialReference: data.spatialReference || { wkid: 4326 },
      tileInfo: data.tileInfo,
      fullExtent: data.fullExtent || { xmin: -180, ymin: -90, xmax: 180, ymax: 90 },
    };
  }

  async getStatus(): Promise<MIServerStatus> {
    const start = Date.now();
    try {
      const response = await fetch(this.options.url, {
        method: 'HEAD',
        headers: this.options.headers,
      }).catch(() => fetch(this.options.url, { headers: this.options.headers }));

      return {
        serverId: this.options.url,
        connected: response.ok || response.status === 405,
        lastChecked: new Date(),
        latencyMs: Date.now() - start,
      };
    } catch (err: any) {
      return {
        serverId: this.options.url,
        connected: false,
        lastChecked: new Date(),
        latencyMs: Date.now() - start,
        error: err.message,
      };
    }
  }

  async getMetadata(): Promise<Record<string, any>> {
    switch (this.options.type) {
      case '3dtiles':
        return this.getTileset();
      case 'arcgis-map':
      case 'arcgis-image':
      case 'arcgis-feature':
        return this.getArcGISServiceInfo();
      case 'cesium-ion':
        return { type: 'cesium-ion', url: this.options.url };
      default:
        return { type: this.options.type, url: this.options.url };
    }
  }

  getOptions(): MIExternalTileServerOptions {
    return { ...this.options };
  }

  private applyAuth(headers: Record<string, string>): void {
    if (!this.options.auth) return;
    switch (this.options.auth.type) {
      case 'api-key':
        headers[this.options.auth.apiKeyHeader || 'X-API-Key'] = this.options.auth.apiKey || '';
        break;
      case 'bearer':
        headers['Authorization'] = `Bearer ${this.options.auth.token}`;
        break;
      case 'basic':
        headers['Authorization'] = `Basic ${btoa(`${this.options.auth.username}:${this.options.auth.password}`)}`;
        break;
    }
  }
}
